import { type PositionResponse, type StateResponse } from '@on3/api';
import { Filter } from '@on3/ui-lib/components/Filter/Filter';
import { useSite } from '@on3/ui-lib/index';
import { addAllToList } from '@on3/ui-lib/utils/addAllToList';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { useRouter } from 'next/router';

import styles from './PlayerRankingsFilters.module.scss';

interface PlayerRankingsFiltersProps {
  years: number[];
  positions: PositionResponse[];
  states: StateResponse[];
  isCollegeRanking?: boolean;
}
export const PlayerRankingsFilters = ({
  years,
  positions,
  states,
  isCollegeRanking = false,
}: PlayerRankingsFiltersProps) => {
  const { sportsData } = useSite();
  const { formatHref } = useDynamicRoute();
  const router = useRouter();
  const {
    position: positionAbbr,
    sport,
    state: stateAbbr,
    year,
    type,
  } = router.query as Record<string, string | undefined>;

  const positionsList = addAllToList(positions, 'Positions');
  const statesList = addAllToList(states?.slice(0, 51), 'States');
  const rankedSports = sportsData?.filter((s) => s.isRankable);
  const isIndustry = router.asPath?.includes('industry-comparison');

  return (
    <div className={styles.filterWrapper}>
      {rankedSports && (
        <Filter
          name="sport"
          navigate={(_, v) =>
            formatHref({
              params: {
                sport: v,
              },
              options: {
                clearQuery: true,
              },
            })
          }
          options={rankedSports.map((s) => ({
            label: s.name as string,
            value: slugify(s.name as string),
          }))}
          value={sport || ''}
        />
      )}

      {years && (
        <Filter
          name="year"
          navigate={(_, v) =>
            formatHref({
              params: {
                year: v,
              },
            })
          }
          options={years.map((y) => ({
            label: String(y),
            value: String(y),
          }))}
          value={year || ''}
        />
      )}
      {positionsList && (
        <Filter
          name="position"
          navigate={(_, v) =>
            formatHref({
              query: {
                position: v || undefined,
              },
            })
          }
          options={positions.map((p) => ({
            label: p.name ?? '',
            value: p.abbreviation?.toLowerCase() as string,
          }))}
          placeholder="All Positions"
          value={positionAbbr || ''}
        />
      )}
      {statesList && !isCollegeRanking && (
        <Filter
          name="state"
          navigate={(_, v) =>
            formatHref({
              query: {
                state: v || undefined,
              },
            })
          }
          options={states.map((s) => ({
            label: s.name ?? '',
            value: s.abbreviation?.toLowerCase() as string,
          }))}
          placeholder="All States"
          value={stateAbbr || ''}
        />
      )}
      {!isIndustry && !isCollegeRanking && (
        <Filter
          name="type"
          navigate={(_, v) =>
            formatHref({
              query: {
                type: v || undefined,
              },
            })
          }
          options={[
            { label: 'High School', value: '' },
            { label: 'JUCO', value: 'juniorcollege' },
          ]}
          value={type || ''}
        />
      )}
    </div>
  );
};
